/**
 * cacheManager.js
 * Comprehensive cache and session manager for GetSettled Customer Portal.
 * Handles automatic clearing of cache, storage, and tokens on logout and tab close.
 */

export const LANGUAGE_STORAGE_KEY = 'getsettled_customer_portal_lang';
export const TAB_SESSION_ACTIVE_KEY = 'gs_tab_session_active';
const AUTH_SYNC_CHANNEL_NAME = 'getsettled_auth_sync';

let broadcastChannel = null;
if (typeof window !== 'undefined' && typeof BroadcastChannel !== 'undefined') {
  try {
    broadcastChannel = new BroadcastChannel(AUTH_SYNC_CHANNEL_NAME);
  } catch (e) {
    console.warn('BroadcastChannel not supported or restricted:', e);
  }
}

/**
 * Clears browser CacheStorage API (Service Worker, HTTP caches, etc.).
 */
export async function clearBrowserCaches() {
  if (typeof window !== 'undefined' && 'caches' in window) {
    try {
      const keys = await window.caches.keys();
      await Promise.all(keys.map((k) => window.caches.delete(k)));
    } catch (e) {
      console.warn('Failed to clear browser CacheStorage:', e);
    }
  }
}

/**
 * Clears all client-side cache and storage.
 * @param {{ preserveLanguage?: boolean }} [options]
 */
export function clearAllAppCache({ preserveLanguage = true } = {}) {
  let savedLang = null;
  if (preserveLanguage && typeof localStorage !== 'undefined') {
    try {
      savedLang = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    } catch {
      // ignore
    }
  }

  // 1. Clear sessionStorage completely
  if (typeof sessionStorage !== 'undefined') {
    try {
      sessionStorage.clear();
    } catch (e) {
      console.warn('Failed to clear sessionStorage:', e);
    }
  }

  // 2. Clear localStorage completely (or all non-language keys)
  if (typeof localStorage !== 'undefined') {
    try {
      localStorage.clear();
      if (savedLang) {
        localStorage.setItem(LANGUAGE_STORAGE_KEY, savedLang);
      }
    } catch (e) {
      console.warn('Failed to clear localStorage:', e);
    }
  }

  // 3. Clear window.caches
  clearBrowserCaches().catch(() => {});
}

/**
 * Broadcasts a logout event across all open tabs of the application.
 */
export function broadcastLogout() {
  if (broadcastChannel) {
    try {
      broadcastChannel.postMessage({ type: 'LOGOUT', timestamp: Date.now() });
    } catch {
      // ignore
    }
  }
}

/**
 * Initializes tab-close session lifecycle and multi-tab synchronization.
 * - On brand new tab open (previous tab was closed): clears any stale localStorage cache.
 * - On receiving a logout event from another tab: clears cache and redirects to /login.
 * - On beforeunload: marks session state so reload survives while tab close ends session.
 */
export function initTabSessionLifecycle(onRemoteLogout) {
  if (typeof window === 'undefined') return () => {};

  // 1. Fresh Tab Check:
  // If this tab doesn't have an active session marker in sessionStorage,
  // that means the previous tab was closed or this is a fresh window.
  // Purge any lingering stale cache from localStorage (preserving user language).
  if (typeof sessionStorage !== 'undefined') {
    try {
      const isActive = sessionStorage.getItem(TAB_SESSION_ACTIVE_KEY);
      if (!isActive) {
        clearAllAppCache({ preserveLanguage: true });
        sessionStorage.setItem(TAB_SESSION_ACTIVE_KEY, '1');
      }
    } catch (e) {
      console.warn('Failed to verify tab session state:', e);
    }
  }

  // 2. Multi-tab Logout Listener
  const handleMessage = (event) => {
    if (event?.data?.type === 'LOGOUT') {
      clearAllAppCache({ preserveLanguage: true });
      if (typeof onRemoteLogout === 'function') {
        onRemoteLogout();
      } else {
        window.location.href = '/login';
      }
    }
  };

  if (broadcastChannel) {
    broadcastChannel.addEventListener('message', handleMessage);
  }

  // 3. Cleanup listener on beforeunload/pagehide
  // Ensure that no sensitive tokens linger in localStorage when unloading
  const handleUnload = () => {
    try {
      // Clean any accidental tokens or cached items in localStorage
      if (typeof localStorage !== 'undefined') {
        localStorage.removeItem('authToken');
        localStorage.removeItem('authUser');
        localStorage.removeItem('user');
        localStorage.removeItem('authLastActivityAt');
        localStorage.removeItem('promotions_list');
      }
    } catch {
      // ignore
    }
  };

  window.addEventListener('pagehide', handleUnload);
  window.addEventListener('beforeunload', handleUnload);

  return () => {
    if (broadcastChannel) {
      broadcastChannel.removeEventListener('message', handleMessage);
    }
    window.removeEventListener('pagehide', handleUnload);
    window.removeEventListener('beforeunload', handleUnload);
  };
}
